import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import UnionSignupLayout, {
  NAVY, AMBER, WHITE, FONT_HEAD, StepPills,
} from './UnionSignupLayout';

const TRADES = [
  { value: 'Electrician',         key: 'electrician' },
  { value: 'Plumber',             key: 'plumber' },
  { value: 'Welder',              key: 'welder' },
  { value: 'HVAC Technician',     key: 'hvacTechnician' },
  { value: 'Carpenter',           key: 'carpenter' },
  { value: 'Pipefitter',          key: 'pipefitter' },
  { value: 'Ironworker',          key: 'ironworker' },
  { value: 'Sheet Metal Worker',  key: 'sheetMetalWorker' },
  { value: 'Millwright',          key: 'millwright' },
  { value: 'CNC Operator',        key: 'cncOperator' },
  { value: 'Industrial Maintenance', key: 'industrialMaintenance' },
  { value: 'Machine Operator',    key: 'machineOperator' },
  { value: 'General Labor',       key: 'generalLabor' },
  { value: 'Multiple Trades',     key: 'multipleTrades' },
];

const inp = {
  width: '100%', padding: '11px 13px', fontSize: '0.95rem',
  border: '1.5px solid #E2E6EB', borderRadius: '8px',
  color: NAVY, outline: 'none', backgroundColor: WHITE,
};
const sel = { ...inp, cursor: 'pointer' };
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '5px' };
const fld = { marginBottom: '1.1rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.1rem' };
const card = {
  backgroundColor: WHITE, borderRadius: '16px',
  boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
  padding: '2rem', marginBottom: '1.25rem',
};

export default function UnionStep1() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form, setForm] = useState({
    unionName: '', localNumber: '', trade: '', email: '', password: '', confirmPassword: '', ageConfirmed: false,
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleContinue = async () => {
    if (!form.unionName || !form.trade || !form.email || !form.password || !form.confirmPassword) {
      alert(t('union.signup.step1.requiredFieldsAlert'));
      return;
    }
    if (form.password.length < 8) {
      alert(t('union.signup.step1.passwordLengthAlert'));
      return;
    }
    if (form.password !== form.confirmPassword) {
      alert(t('employer.signup.step1.passwordMismatchAlert'));
      return;
    }
    if (!form.ageConfirmed) {
      alert(t('worker.signup.step1.ageAlert'));
      return;
    }

    const { error } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: {
          role: 'union',
          union_name: form.unionName,
          local_number: form.localNumber,
          trade: form.trade,
        },
      },
    });
    if (error) { alert(error.message); return; }
    navigate('/union/signup/step2');
  };

  return (
    <UnionSignupLayout
      step={1}
      tipText={t('union.signup.step1.tip')}
      onContinue={handleContinue}
    >
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        {t('union.signup.step1.eyebrow')}
      </div>

      <StepPills current={1} />

      <div style={card}>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.1rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.35rem' }}>
          {t('union.signup.step1.title')}
        </h1>
        <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.52)', marginBottom: '1.75rem', lineHeight: 1.5 }}>
          {t('union.signup.step1.subtitle')}
        </p>

        {/* Union name */}
        <div style={fld}>
          <label style={lbl}>{t('union.signup.step1.unionNameLabel')}</label>
          <input style={inp} placeholder={t('union.signup.step1.unionNamePlaceholder')} value={form.unionName} onChange={e => set('unionName', e.target.value)} />
        </div>

        {/* Local number */}
        <div style={fld}>
          <label style={lbl}>{t('union.signup.step1.localNumberLabel')} <span style={{ fontWeight: 400, color: 'rgba(13,27,42,0.45)' }}>{t('union.signup.step1.optional')}</span></label>
          <input style={inp} placeholder={t('union.signup.step1.localNumberPlaceholder')} value={form.localNumber} onChange={e => set('localNumber', e.target.value)} />
        </div>

        {/* Trade represented */}
        <div style={fld}>
          <label style={lbl}>{t('union.signup.step1.tradeRepresentedLabel')}</label>
          <select style={sel} value={form.trade} onChange={e => set('trade', e.target.value)}>
            <option value="">{t('union.signup.step1.tradeRepresentedPlaceholder')}</option>
            {TRADES.map(tr => <option key={tr.value} value={tr.value}>{t(`worker.tradeNames.${tr.key}`)}</option>)}
          </select>
        </div>

        {/* Email */}
        <div style={fld}>
          <label style={lbl}>{t('union.signup.step1.emailLabel')}</label>
          <input style={inp} type="email" placeholder={t('union.signup.step1.emailPlaceholder')} value={form.email} onChange={e => set('email', e.target.value)} />
        </div>

        {/* Password + Confirm */}
        <div style={g2}>
          <div>
            <label style={lbl}>{t('worker.signup.step1.passwordLabel')}</label>
            <input style={inp} type="password" placeholder={t('worker.signup.step1.passwordPlaceholder')} value={form.password} onChange={e => set('password', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>{t('worker.signup.step1.confirmPasswordLabel')}</label>
            <input style={inp} type="password" placeholder={t('worker.signup.step1.confirmPasswordPlaceholder')} value={form.confirmPassword} onChange={e => set('confirmPassword', e.target.value)} />
          </div>
        </div>

        {/* Age confirmation */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '0.85rem', color: 'rgba(13,27,42,0.7)', lineHeight: 1.5 }}>
          <input
            type="checkbox"
            checked={form.ageConfirmed}
            onChange={e => set('ageConfirmed', e.target.checked)}
            style={{ marginTop: '2px', accentColor: AMBER, width: '16px', height: '16px', flexShrink: 0 }}
          />
          <span>{t('worker.signup.step1.ageConfirmLabel')}</span>
        </label>
      </div>
    </UnionSignupLayout>
  );
}
